import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Clock, Check, X, Calendar as CalendarIcon, Trash2, Play } from 'lucide-react';
import { usePilatesStore } from '../store/pilatesStore';
import type { ScheduledWorkout } from '../types';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function Calendar() {
  const { videos, scheduledWorkouts, scheduleWorkout, completeWorkoutAndLog, deleteScheduledWorkout, addSession } = usePilatesStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [selectedTime, setSelectedTime] = useState('09:00');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const calendarDays = useMemo(() => {
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [year, month, firstDayWeekday, daysInMonth]);

  const getWorkoutsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return scheduledWorkouts.filter(w => w.scheduledDate.split('T')[0] === dateStr);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setShowScheduleModal(true);
  };

  const handleScheduleWorkout = () => {
    if (!selectedDate || !selectedVideo) return;

    const newWorkout: ScheduledWorkout = {
      id: Date.now().toString(),
      videoId: selectedVideo,
      scheduledDate: selectedDate.toISOString(),
      scheduledTime: selectedTime,
      completed: false,
      reminder: true
    };

    scheduleWorkout(newWorkout);
    setShowScheduleModal(false);
    setSelectedVideo('');
    setSelectedTime('09:00');
  };

  const handleCompleteWorkout = (workoutId: string) => {
    // This now logs a session and marks the workout as complete
    completeWorkoutAndLog(workoutId);
  };

  const handleDeleteWorkout = (workoutId: string) => {
    deleteScheduledWorkout(workoutId);
  };

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return (
    <div className="min-h-screen pb-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Workout Calendar
            </h1>
            <p className="text-xl text-white/90">
              Plan and schedule your pilates sessions
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          {/* Calendar Header */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={goToPreviousMonth}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </motion.button>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  {MONTHS[month]} {year}
                </h2>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={goToNextMonth}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </motion.button>
            </div>
          </div>

          {/* Days Header */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {DAYS.map((day) => (
              <div
                key={day}
                className="px-2 py-3 text-center text-sm font-semibold text-gray-600"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map(({ date, isCurrentMonth }, index) => {
              const dateStr = date.toISOString().split('T')[0];
              const isToday = dateStr === todayStr;
              const workouts = getWorkoutsForDate(date);
              const hasWorkouts = workouts.length > 0;

              return (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => handleDateClick(date)}
                  className={`min-h-[100px] md:min-h-[120px] p-2 border-b border-r border-gray-100 cursor-pointer transition-colors ${
                    !isCurrentMonth ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        isToday
                          ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white'
                          : isCurrentMonth
                          ? 'text-gray-900'
                          : 'text-gray-400'
                      }`}
                    >
                      {date.getDate()}
                    </span>
                    {workouts.length > 0 && (
                      <span className="w-2 h-2 bg-rose-500 rounded-full" />
                    )}
                  </div>

                  {/* Workout indicators */}
                  <div className="mt-2 space-y-1">
                    {workouts.slice(0, 2).map((workout) => {
                      const video = videos.find(v => v.id === workout.videoId);
                      return (
                        <div
                          key={workout.id}
                          className={`text-xs px-2 py-1 rounded-lg truncate ${
                            workout.completed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {video?.title.slice(0, 15)}...
                        </div>
                      );
                    })}
                    {workouts.length > 2 && (
                      <div className="text-xs text-gray-500 px-2">
                        +{workouts.length - 2} more
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Upcoming Workouts List */}
        {scheduledWorkouts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">All Scheduled Workouts</h3>
            <div className="space-y-3">
              {scheduledWorkouts
                .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                .map((workout) => {
                  const video = videos.find(v => v.id === workout.videoId);
                  const workoutDate = new Date(workout.scheduledDate);
                  const isPast = workoutDate < today;

                  return (
                    <motion.div
                      key={workout.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex items-center gap-4 p-4 rounded-xl ${
                        workout.completed
                          ? 'bg-green-50 border border-green-200'
                          : isPast
                          ? 'bg-gray-50 border border-gray-200'
                          : 'bg-rose-50 border border-rose-200'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{video?.title}</p>
                        <p className="text-sm text-gray-600">
                          {workoutDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {' '}at {workout.scheduledTime}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!workout.completed && (
                          <>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => window.open(video?.videoUrl, '_blank')}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                            >
                              <Play className="w-4 h-4" />
                              Watch
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleCompleteWorkout(workout.id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Done
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteWorkout(workout.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </motion.button>
                          </>
                        )}
                        {workout.completed && (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <Check className="w-4 h-4" />
                            Completed
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showScheduleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowScheduleModal(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">Schedule Workout</h3>
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {selectedDate && (
                  <p className="text-white/90 mt-1">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Video Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Workout
                  </label>
                  <select
                    value={selectedVideo}
                    onChange={(e) => setSelectedVideo(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-300 focus:ring-0 focus:outline-none transition-colors"
                  >
                    <option value="">Choose a workout...</option>
                    {videos.map((video) => (
                      <option key={video.id} value={video.id}>
                        {video.title} ({video.duration} min - {video.difficulty})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Time
                  </label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-300 focus:ring-0 focus:outline-none transition-colors"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleScheduleWorkout}
                    disabled={!selectedVideo}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Schedule
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
